import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowUpRight, ChevronRight, RefreshCw } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { Link } from "wouter";
import { SocialPricingSection } from "@/components/SocialPricingSection";

/* ─── Service Illustrations ───────────────────────────── */
const IlluWeb = () => (
  <svg viewBox="0 0 140 100" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="8" width="120" height="72" rx="6" fill="#e0f2fe" stroke="#38bdf8" strokeWidth="1.5"/>
    <rect x="10" y="8" width="120" height="20" rx="6" fill="#38bdf8"/>
    <rect x="10" y="22" width="120" height="6" fill="#38bdf8"/>
    <circle cx="24" cy="18" r="3" fill="rgba(255,255,255,0.55)"/>
    <circle cx="35" cy="18" r="3" fill="rgba(255,255,255,0.55)"/>
    <circle cx="46" cy="18" r="3" fill="rgba(255,255,255,0.55)"/>
    <rect x="20" y="37" width="40" height="4" rx="2" fill="#38bdf8" opacity="0.8"/>
    <rect x="26" y="47" width="58" height="4" rx="2" fill="#0ea5e9" opacity="0.5"/>
    <rect x="20" y="57" width="48" height="4" rx="2" fill="#38bdf8" opacity="0.65"/>
    <rect x="26" y="67" width="28" height="4" rx="2" fill="#0ea5e9" opacity="0.4"/>
    <rect x="88" y="32" width="32" height="44" rx="3" fill="#0ea5e9" opacity="0.12"/>
    <rect x="93" y="40" width="20" height="3" rx="1.5" fill="#38bdf8" opacity="0.75"/>
    <rect x="93" y="49" width="14" height="3" rx="1.5" fill="#38bdf8" opacity="0.5"/>
    <rect x="93" y="57" width="18" height="3" rx="1.5" fill="#38bdf8" opacity="0.7"/>
    <rect x="93" y="65" width="10" height="3" rx="1.5" fill="#38bdf8" opacity="0.45"/>
    <rect x="60" y="80" width="20" height="8" rx="2" fill="#38bdf8" opacity="0.3"/>
    <rect x="48" y="87" width="44" height="4" rx="2" fill="#38bdf8" opacity="0.25"/>
  </svg>
);

const IlluDesign = () => (
  <svg viewBox="0 0 140 100" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect x="25" y="12" width="90" height="70" rx="6" fill="#f0f9ff" stroke="#38bdf8" strokeWidth="1.5"/>
    <rect x="25" y="12" width="90" height="16" rx="6" fill="#e0f2fe"/>
    <rect x="25" y="22" width="90" height="6" fill="#e0f2fe"/>
    <circle cx="56" cy="52" r="18" fill="none" stroke="#38bdf8" strokeWidth="1.5" strokeDasharray="4 2"/>
    <circle cx="56" cy="38" r="8" fill="#EF4444" opacity="0.75"/>
    <circle cx="68" cy="56" r="8" fill="#F59E0B" opacity="0.75"/>
    <circle cx="44" cy="56" r="8" fill="#38bdf8" opacity="0.75"/>
    <circle cx="56" cy="52" r="7" fill="white" opacity="0.9"/>
    <rect x="86" y="36" width="20" height="4" rx="2" fill="#EF4444" opacity="0.6"/>
    <rect x="86" y="46" width="20" height="4" rx="2" fill="#F59E0B" opacity="0.6"/>
    <rect x="86" y="56" width="20" height="4" rx="2" fill="#38bdf8" opacity="0.6"/>
    <rect x="86" y="66" width="20" height="4" rx="2" fill="#8B5CF6" opacity="0.6"/>
    <line x1="76" y1="88" x2="100" y2="70" stroke="#64748b" strokeWidth="2" strokeLinecap="round"/>
    <rect x="100" y="64" width="6" height="14" rx="2" fill="#64748b" transform="rotate(45 100 64)"/>
  </svg>
);

const IlluMarketing = () => (
  <svg viewBox="0 0 140 100" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect x="10" y="62" width="18" height="28" rx="3" fill="#38bdf8" opacity="0.5"/>
    <rect x="35" y="48" width="18" height="42" rx="3" fill="#0ea5e9" opacity="0.7"/>
    <rect x="60" y="32" width="18" height="58" rx="3" fill="#38bdf8" opacity="0.9"/>
    <rect x="85" y="18" width="18" height="72" rx="3" fill="#0284c7"/>
    <polyline points="19,62 44,48 69,32 94,18" stroke="#38bdf8" strokeWidth="2" strokeDasharray="4 2" fill="none"/>
    <circle cx="19" cy="62" r="4" fill="#38bdf8"/>
    <circle cx="44" cy="48" r="4" fill="#0ea5e9"/>
    <circle cx="69" cy="32" r="4" fill="#38bdf8"/>
    <circle cx="94" cy="18" r="4" fill="#0284c7"/>
    <path d="M115 22 L125 12 L128 22 L118 25 Z" fill="#38bdf8" opacity="0.8"/>
    <path d="M113 28 C118 22 122 16 125 12" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
    <line x1="10" y1="90" x2="130" y2="90" stroke="#94a3b8" strokeWidth="1.5"/>
    <line x1="10" y1="90" x2="10" y2="10" stroke="#94a3b8" strokeWidth="1.5"/>
  </svg>
);

const IlluSocial = () => (
  <svg viewBox="0 0 140 100" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <circle cx="70" cy="50" r="12" fill="#38bdf8" opacity="0.9"/>
    <circle cx="30" cy="25" r="9" fill="#0ea5e9" opacity="0.7"/>
    <circle cx="110" cy="25" r="9" fill="#0ea5e9" opacity="0.7"/>
    <circle cx="25" cy="72" r="9" fill="#38bdf8" opacity="0.6"/>
    <circle cx="115" cy="72" r="9" fill="#38bdf8" opacity="0.6"/>
    <circle cx="70" cy="88" r="9" fill="#0284c7" opacity="0.7"/>
    <line x1="70" y1="38" x2="30" y2="34" stroke="#38bdf8" strokeWidth="1.5" opacity="0.5"/>
    <line x1="70" y1="38" x2="110" y2="34" stroke="#38bdf8" strokeWidth="1.5" opacity="0.5"/>
    <line x1="58" y1="52" x2="34" y2="63" stroke="#38bdf8" strokeWidth="1.5" opacity="0.5"/>
    <line x1="82" y1="52" x2="106" y2="63" stroke="#38bdf8" strokeWidth="1.5" opacity="0.5"/>
    <line x1="65" y1="62" x2="70" y2="79" stroke="#38bdf8" strokeWidth="1.5" opacity="0.5"/>
    <text x="70" y="54" textAnchor="middle" fontSize="9" fill="white" fontWeight="bold">ME</text>
    <text x="30" y="29" textAnchor="middle" fontSize="8" fill="white" fontWeight="bold">f</text>
    <text x="110" y="29" textAnchor="middle" fontSize="8" fill="white" fontWeight="bold">in</text>
    <text x="25" y="76" textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">ig</text>
    <text x="115" y="76" textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">wa</text>
    <text x="70" y="92" textAnchor="middle" fontSize="7" fill="white" fontWeight="bold">tw</text>
  </svg>
);

const IlluEcommerce = () => (
  <svg viewBox="0 0 140 100" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect x="8" y="20" width="55" height="65" rx="5" fill="#e0f2fe" stroke="#38bdf8" strokeWidth="1.5"/>
    <rect x="8" y="20" width="55" height="16" rx="5" fill="#38bdf8"/>
    <rect x="8" y="29" width="55" height="7" fill="#38bdf8"/>
    <rect x="16" y="46" width="38" height="4" rx="2" fill="#38bdf8" opacity="0.6"/>
    <rect x="16" y="56" width="28" height="4" rx="2" fill="#0ea5e9" opacity="0.4"/>
    <rect x="16" y="66" width="33" height="4" rx="2" fill="#38bdf8" opacity="0.5"/>
    <rect x="16" y="76" width="20" height="4" rx="2" fill="#0ea5e9" opacity="0.4"/>
    <path d="M78 22 L92 22 L98 52 L120 52" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
    <rect x="80" y="20" width="12" height="4" rx="2" fill="#38bdf8" opacity="0.5"/>
    <circle cx="98" cy="62" r="6" fill="#38bdf8"/>
    <circle cx="118" cy="62" r="6" fill="#38bdf8"/>
    <rect x="96" y="34" width="28" height="22" rx="4" fill="#38bdf8" opacity="0.15" stroke="#38bdf8" strokeWidth="1"/>
    <rect x="100" y="38" width="8" height="8" rx="2" fill="#38bdf8" opacity="0.5"/>
    <rect x="112" y="38" width="8" height="8" rx="2" fill="#0ea5e9" opacity="0.5"/>
    <text x="80" y="88" fontSize="9" fill="#38bdf8" fontWeight="600" opacity="0.8">$</text>
    <rect x="86" y="82" width="40" height="10" rx="4" fill="#38bdf8" opacity="0.15" stroke="#38bdf8" strokeWidth="1"/>
    <rect x="90" y="85" width="28" height="4" rx="2" fill="#38bdf8" opacity="0.5"/>
  </svg>
);

const IlluApps = () => (
  <svg viewBox="0 0 140 100" fill="none" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <rect x="48" y="8" width="44" height="78" rx="8" fill="#e0f2fe" stroke="#38bdf8" strokeWidth="1.5"/>
    <rect x="52" y="16" width="36" height="56" rx="4" fill="#f0f9ff"/>
    <circle cx="70" cy="82" r="3.5" fill="#38bdf8" opacity="0.6"/>
    <rect x="62" y="10" width="16" height="3" rx="1.5" fill="#38bdf8" opacity="0.4"/>
    <rect x="55" y="19" width="14" height="14" rx="3" fill="#38bdf8" opacity="0.7"/>
    <rect x="72" y="19" width="14" height="14" rx="3" fill="#0ea5e9" opacity="0.6"/>
    <rect x="55" y="36" width="14" height="14" rx="3" fill="#0284c7" opacity="0.5"/>
    <rect x="72" y="36" width="14" height="14" rx="3" fill="#38bdf8" opacity="0.7"/>
    <rect x="55" y="53" width="14" height="14" rx="3" fill="#0ea5e9" opacity="0.5"/>
    <rect x="72" y="53" width="14" height="14" rx="3" fill="#0284c7" opacity="0.6"/>
    <rect x="8" y="30" width="30" height="50" rx="5" fill="#dbeafe" stroke="#38bdf8" strokeWidth="1" opacity="0.7"/>
    <rect x="12" y="38" width="22" height="3" rx="1.5" fill="#38bdf8" opacity="0.5"/>
    <rect x="12" y="46" width="16" height="3" rx="1.5" fill="#38bdf8" opacity="0.4"/>
    <rect x="12" y="54" width="20" height="3" rx="1.5" fill="#38bdf8" opacity="0.5"/>
    <rect x="102" y="30" width="30" height="50" rx="5" fill="#dbeafe" stroke="#38bdf8" strokeWidth="1" opacity="0.7"/>
    <rect x="106" y="38" width="22" height="3" rx="1.5" fill="#38bdf8" opacity="0.5"/>
    <rect x="106" y="46" width="16" height="3" rx="1.5" fill="#38bdf8" opacity="0.4"/>
    <rect x="106" y="54" width="20" height="3" rx="1.5" fill="#38bdf8" opacity="0.5"/>
  </svg>
);

/* ─── Tool Icons ───────────────────────────────────────── */
const toolsList = [
  {
    name: "Google Business",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
  },
  {
    name: "Search Console",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#458CF5"/><circle cx="10.5" cy="10.5" r="4" stroke="white" strokeWidth="1.8" fill="none"/><line x1="13.5" y1="13.5" x2="17" y2="17" stroke="white" strokeWidth="1.8" strokeLinecap="round"/></svg>
  },
  {
    name: "Meta Pixel",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#0866FF"/><path d="M4 12c0-2.5 1.5-4.5 3.5-4.5 1.2 0 2 .6 3 2 .8-1.2 1.8-2 3-2 2 0 3.5 2 3.5 4.5s-1.2 4-2.8 5c-.8.5-1.5.5-2-.5L12 15c-.5-1-1-1-1.5 0l-1.2 1.5c-.5 1-1.2 1-2 .5C5.2 16 4 14.5 4 12z" fill="white"/></svg>
  },
  {
    name: "Google Analytics",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#F9AB00"/><rect x="5" y="12" width="4" height="7" rx="1" fill="white" opacity="0.5"/><rect x="10" y="8" width="4" height="11" rx="1" fill="white" opacity="0.7"/><rect x="15" y="5" width="4" height="14" rx="1" fill="white"/></svg>
  },
  {
    name: "Google Ads",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#4285F4"/><path d="M12 5L7 14h10L12 5z" fill="white" opacity="0.9"/><circle cx="6" cy="17" r="2" fill="#FBBC05"/><circle cx="18" cy="17" r="2" fill="#34A853"/></svg>
  },
  {
    name: "Mailchimp",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#FFE01B"/><text x="5.5" y="17" fontSize="12" fontWeight="bold" fontFamily="Arial" fill="#241C15">M</text></svg>
  },
  {
    name: "WordPress",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="11" fill="#21759B"/><path d="M3.9 12c0-1.37.28-2.67.78-3.86l4.27 11.69C6.07 18.62 3.9 15.55 3.9 12zm14.24-.52c0-.85-.31-1.44-.57-1.9-.35-.57-.68-1.05-.68-1.62 0-.63.48-1.22 1.16-1.22l.09.01A8.1 8.1 0 0 0 12 3.9a8.1 8.1 0 0 0-6.85 3.76l.52.02c.85 0 2.16-.1 2.16-.1.44-.03.49.62.05.67 0 0-.44.05-.93.08l2.96 8.8 1.78-5.33-1.27-3.47c-.44-.03-.85-.08-.85-.08-.44-.03-.39-.7.05-.67 0 0 1.34.1 2.13.1.85 0 2.16-.1 2.16-.1.44-.03.49.62.05.67 0 0-.44.05-.93.08l2.94 8.74.81-2.71c.35-.98.55-1.69.55-2.3zm-5.72 1.2-2.44 7.09c.73.21 1.5.33 2.29.33a8.1 8.1 0 0 0 2.37-.35l-.05-.09zm6.98-4.61c.03.23.05.47.05.73 0 .72-.13 1.52-.54 2.53l-2.16 6.25A8.1 8.1 0 0 0 20.1 12c0-.68-.08-1.34-.2-1.93z" fill="white"/></svg>
  },
  {
    name: "PHP",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#777BB4"/><text x="3" y="16" fontSize="9" fontWeight="bold" fontFamily="Arial" fill="white">PHP</text></svg>
  },
  {
    name: "React",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#20232a"/><ellipse cx="12" cy="12" rx="2" ry="2" fill="#61DAFB"/><ellipse cx="12" cy="12" rx="9" ry="3.5" stroke="#61DAFB" strokeWidth="1.2" fill="none"/><ellipse cx="12" cy="12" rx="9" ry="3.5" stroke="#61DAFB" strokeWidth="1.2" fill="none" transform="rotate(60 12 12)"/><ellipse cx="12" cy="12" rx="9" ry="3.5" stroke="#61DAFB" strokeWidth="1.2" fill="none" transform="rotate(120 12 12)"/></svg>
  },
  {
    name: "HTML",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#E34F26"/><text x="2.5" y="16" fontSize="8" fontWeight="bold" fontFamily="Arial" fill="white">HTML</text></svg>
  },
  {
    name: "Shopify",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#95BF47"/><path d="M15.5 6.5c-.1-.1-.3-.1-.4 0l-.9.3c-.2-.6-.6-1.1-1.2-1.1-.1 0-.2 0-.4.1-.1-.2-.4-.4-.7-.4C10.8 5.4 10 7 9.8 8.1L8.5 8.5c-.4.1-.4.1-.4.5L7.5 16h7.7L16 7c0-.2-.2-.4-.5-.5zM13 5.9c.3 0 .5.2.7.5-.3.1-.7.2-1 .3.1-.4.2-.8.3-.8zm-.6-.3c.1 0 .2 0 .3.1-.1 0-.5.4-.7 1.1l-1 .3c.2-.8.8-1.5 1.4-1.5zm1.1 10.2H9.1l.7-4.6 1.4-.4v.8c0 .2.1.3.3.3.2 0 .3-.1.3-.3V11l1-.3v5.1z" fill="white"/></svg>
  },
  {
    name: "Inteligencia Artificial",
    icon: <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#6366F1"/><circle cx="12" cy="12" r="3.5" fill="white" opacity="0.9"/><circle cx="12" cy="5" r="1.5" fill="white" opacity="0.7"/><circle cx="12" cy="19" r="1.5" fill="white" opacity="0.7"/><circle cx="5" cy="12" r="1.5" fill="white" opacity="0.7"/><circle cx="19" cy="12" r="1.5" fill="white" opacity="0.7"/><line x1="12" y1="8.5" x2="12" y2="6.5" stroke="white" strokeWidth="1" opacity="0.6"/><line x1="12" y1="15.5" x2="12" y2="17.5" stroke="white" strokeWidth="1" opacity="0.6"/><line x1="8.5" y1="12" x2="6.5" y2="12" stroke="white" strokeWidth="1" opacity="0.6"/><line x1="15.5" y1="12" x2="17.5" y2="12" stroke="white" strokeWidth="1" opacity="0.6"/></svg>
  }
];

const projects = [
  { id: 1, img: "/assets/portafolio/riqui-gell.jpg", title: "Riqui Gell", category: "Página Web" },
  { id: 2, img: "/assets/portafolio/operacion-bendicion.jpg", title: "Operación Bendición", category: "Landing Page" },
  { id: 3, img: "/assets/portafolio/samuel-y-crisrimeri.jpg", title: "Samuel y Crismeri", category: "Invtiación Digital" },
  { id: 4, img: "/assets/portafolio/distribuidora-Fischer.jpg", title: "Distribuidora Fischer", category: "E-Commerce" },
  { id: 5, img: "/assets/portafolio/manuel-bordas.jpg", title: "Manuel Bordas Blog", category: "Página Web" },
  { id: 6, img: "/assets/portafolio/proyecto-emely.jpg", title: "Proyecto Emely", category: "Página Web" },
];

const services = [
  { key: "web", Illu: IlluWeb },
  { key: "design", Illu: IlluDesign },
  { key: "marketing", Illu: IlluMarketing },
  { key: "social", Illu: IlluSocial },
  { key: "ecommerce", Illu: IlluEcommerce },
  { key: "apps", Illu: IlluApps },
] as const;

export default function Home() {
  const { tr } = useLang();
  const [isAnnual, setIsAnnual] = useState(true);
  const [slide, setSlide] = useState(0);
  const VISIBLE = 3;
  const totalSlides = projects.length - VISIBLE + 1; // 4

  useEffect(() => {
    const t = setInterval(() => {
      setSlide(s => (s + 1) % totalSlides);
    }, 3500);
    return () => clearInterval(t);
  }, [totalSlides]);

  const visibleProjects = [0, 1, 2].map(i => projects[(slide + i) % projects.length]);

  const svcTitles: Record<string, string> = {
    web: tr.services.web,
    design: tr.services.design,
    marketing: tr.services.marketing,
    social: tr.services.social,
    ecommerce: tr.services.ecommerce,
    apps: tr.services.apps,
  };
  const svcDescs: Record<string, string> = {
    web: tr.services.webDesc,
    design: tr.services.designDesc,
    marketing: tr.services.marketingDesc,
    social: tr.services.socialDesc,
    ecommerce: tr.services.ecommerceDesc,
    apps: tr.services.appsDesc,
  };

  return (
    <div className="bg-background text-foreground overflow-x-hidden selection:bg-primary/30">

      {/* ── Hero — Full-width background ── */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="/assets/slide.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0a0f1e]/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 w-full text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter leading-[1.05] mb-8 text-white">
              {tr.hero.title1}<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-blue-400 to-indigo-300">{tr.hero.titleExtra}</span><br />
              {tr.hero.title2}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl mx-auto font-light leading-relaxed">{tr.hero.desc}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link href="/portafolio" className="inline-flex items-center justify-center whitespace-nowrap rounded-full h-14 px-8 text-base font-bold bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_0_20px_rgba(56,189,248,0.4)] group transition-all duration-300">
                {tr.hero.viewWork}<ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link href="/servicios" className="inline-flex items-center justify-center whitespace-nowrap rounded-full h-14 px-8 text-base font-semibold border border-white/30 hover:bg-white/10 text-white transition-colors backdrop-blur-sm">
                {tr.hero.capabilities}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── ¿Qué te ofrecemos? (Light) ── */}
      <section className="py-32 px-6 bg-slate-50 border-y border-slate-200 relative text-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-20 text-center">
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">{tr.services.title}</h2>
            <p className="text-slate-600 text-xl max-w-2xl mx-auto font-light">{tr.services.desc}</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map(({ key, Illu }, i) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group p-8 rounded-2xl bg-white border border-slate-200 hover:border-primary/50 hover:bg-gradient-to-br hover:from-[#e0f2fe] hover:to-[#bae6fd] transition-all duration-400 cursor-default flex flex-col items-center text-center hover:shadow-lg"
              >
                <div className="w-40 h-28 mb-6 flex items-center justify-center">
                  <Illu />
                </div>
                <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">{svcTitles[key]}</h3>
                <p className="text-slate-600 leading-relaxed font-light text-sm">{svcDescs[key]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Proyectos Recientes — Carousel (Dark + blue gradient) ── */}
      <section className="py-32 px-6 text-foreground relative overflow-hidden dark-texture" style={{ background: "linear-gradient(150deg, #0d2137 0%, #0a0f1e 55%, #060c18 100%)" }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 flex flex-col md:flex-row justify-between items-end gap-6">
            <div>
              <h2 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight text-white">{tr.portfolioPreview.title}</h2>
              <p className="text-muted-foreground text-xl max-w-2xl font-light">{tr.portfolioPreview.desc}</p>
            </div>
            <Link href="/portafolio" className="inline-flex items-center text-primary font-semibold hover:text-primary/80 transition-colors whitespace-nowrap">
              {tr.portfolioPreview.allProjects} <ArrowUpRight className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {visibleProjects.map((project, i) => (
                <motion.div
                  key={`${project.id}-${slide}-${i}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: i * 0.07 }}
                >
                  <Link href="/portafolio" className="group cursor-pointer block">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-border relative mb-6">
                      <img src={project.img} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-background/20 group-hover:bg-transparent transition-colors duration-500" />
                    </div>
                    <div className="text-primary font-mono text-xs tracking-widest uppercase mb-2">{project.category}</div>
                    <h3 className="text-2xl font-bold text-white group-hover:text-primary transition-colors">{project.title}</h3>
                  </Link>
                </motion.div>
              ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-10">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`h-2 rounded-full transition-all duration-300 ${i === slide ? 'w-6 bg-primary' : 'w-2 bg-border hover:bg-primary/50'}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Herramientas — Infinite marquee (Light) ── */}
      <section className="py-24 px-6 bg-white border-y border-slate-200 text-slate-900 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{tr.tech.title}</h2>
          <p className="text-slate-500 text-lg max-w-xl mx-auto">{tr.tech.desc}</p>
        </div>

        <div className="relative flex overflow-x-hidden">
          <div className="animate-marquee flex items-center gap-8 py-2 whitespace-nowrap">
            {[...toolsList, ...toolsList].map((tool, i) => (
              <div key={i} className="flex flex-col items-center gap-2 group shrink-0">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-slate-50 border border-slate-200 group-hover:border-primary/40 group-hover:shadow-md transition-all duration-300 overflow-hidden">
                  {tool.icon}
                </div>
                <span className="text-xs text-slate-500 font-medium text-center leading-tight w-16">{tool.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Proceso (Dark + blue gradient) ── */}
      <section
        className="py-32 px-6 text-foreground relative overflow-hidden"
        style={{ background: "linear-gradient(150deg, #0a1e32 0%, #0a0f1e 50%, #060c18 100%)" }}
      >
        <div className="absolute inset-0 grid-texture pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-20 text-center">
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight text-white">{tr.process.title}</h2>
            <p className="text-muted-foreground text-xl max-w-2xl mx-auto font-light">{tr.process.desc}</p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: tr.process.step1, desc: tr.process.step1Desc },
              { step: "02", title: tr.process.step2, desc: tr.process.step2Desc },
              { step: "03", title: tr.process.step3, desc: tr.process.step3Desc },
              { step: "04", title: tr.process.step4, desc: tr.process.step4Desc }
            ].map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative p-6 rounded-2xl bg-white/5 border border-primary/20 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300"
              >
                <div className="text-5xl font-black text-primary/25 mb-4">{p.step}</div>
                <h3 className="text-xl font-bold mb-3 text-white">{p.title}</h3>
                <p className="text-muted-foreground font-light leading-relaxed text-sm">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Precios (Light) ── */}
      <section className="py-32 px-6 bg-slate-50 border-y border-slate-200 text-slate-900">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
            <h2 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight">{tr.pricing.title}</h2>
            <p className="text-slate-600 text-xl max-w-2xl mx-auto font-light mb-10">{tr.pricing.desc}</p>
            <div className="inline-flex items-center p-1 bg-slate-200 rounded-full">
              <button onClick={() => setIsAnnual(false)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all ${!isAnnual ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>{tr.pricing.monthly}</button>
              <button onClick={() => setIsAnnual(true)} className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${isAnnual ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
                {tr.pricing.annual}
                <span className="bg-primary/10 text-primary text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold">{tr.pricing.save}</span>
              </button>
            </div>
            {isAnnual && <p className="text-sm text-slate-500 mt-3">Pago único que incluye hosting y dominio por 1 año completo.</p>}
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Onepage */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col">
              <h3 className="text-xl font-bold mb-2">{tr.pricing.onepage}</h3>
              <div className="my-6 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">${isAnnual ? '250' : '25'}</span>
                <span className="text-slate-500 font-medium">USD{isAnnual ? tr.pricing.year : tr.pricing.month}</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {tr.pricing.onepageFeatures.map((f, j) => (
                  <li key={j} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /><span className="text-slate-600 text-sm">{f}</span></li>
                ))}
              </ul>
              {isAnnual && <div className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-5 text-slate-500"><RefreshCw className="w-3.5 h-3.5 shrink-0" /><span>{tr.pricing.renewalNote}</span></div>}
              <Link href="/contacto" className="inline-flex items-center justify-center rounded-xl text-sm font-semibold h-12 w-full bg-slate-100 text-slate-900 hover:bg-slate-200 transition-colors">{tr.nav.startProject}</Link>
            </motion.div>

            {/* Multipáginas */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-slate-900 rounded-3xl p-8 border-2 border-primary shadow-xl flex flex-col relative text-white transform md:-translate-y-4">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-white text-xs font-bold uppercase tracking-widest py-1.5 px-4 rounded-full">{tr.pricing.popular}</div>
              <h3 className="text-xl font-bold mb-1">{tr.pricing.multipages}</h3>
              <p className="text-slate-400 text-sm mb-2">{tr.pricing.multipagesDesc}</p>
              <div className="my-6 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">${isAnnual ? '400' : '40'}</span>
                <span className="text-slate-400 font-medium">USD{isAnnual ? tr.pricing.year : tr.pricing.month}</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {tr.pricing.multipagesFeatures.map((f, j) => (
                  <li key={j} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-primary shrink-0" /><span className="text-slate-300 text-sm">{f}</span></li>
                ))}
              </ul>
              {isAnnual && <div className="flex items-center gap-2 text-xs bg-white/10 rounded-xl px-3 py-2 mb-5 text-slate-300"><RefreshCw className="w-3.5 h-3.5 shrink-0" /><span>{tr.pricing.renewalNote}</span></div>}
              <Link href="/contacto" className="inline-flex items-center justify-center rounded-xl text-sm font-bold h-12 w-full bg-primary text-white hover:bg-primary/90 transition-colors shadow-[0_0_20px_rgba(56,189,248,0.3)]">{tr.nav.startProject}</Link>
            </motion.div>

            {/* Custom */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm flex flex-col">
              <h3 className="text-xl font-bold mb-2">{tr.pricing.custom}</h3>
              <p className="text-slate-500 text-sm mb-2">{tr.pricing.customDesc}</p>
              <div className="my-6 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold text-slate-800">A Medida</span>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {tr.pricing.customFeatures.map((f, j) => (
                  <li key={j} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-slate-400 shrink-0" /><span className="text-slate-600 text-sm">{f}</span></li>
                ))}
              </ul>
              <Link href="/contacto" className="inline-flex items-center justify-center rounded-xl text-sm font-semibold h-12 w-full bg-slate-900 text-white hover:bg-slate-800 transition-colors">{tr.pricing.contactQuote}</Link>
            </motion.div>
          </div>
        </div>
      </section>

      <SocialPricingSection />
    </div>
  );
}
